import { useRouter } from 'next/router';
import { getLocale } from '@hub/libs/constants';
import { useTranslation } from 'next-i18next';
import { WidenImage } from '@hub/common/Widen/WidenImage';
import { Button } from '@hub/common/Button/Button';
import type { ProductTableProps } from '@hub/bearing-hub/BearingHubPage/BearingsTable/BearingsTable';
import { BearingTableDimensions } from '@hub/bearing-hub/BearingHubPage/BearingsTable/BearingTableDimensions';
import { Icon, IconID } from '@hub/common/Icon/Icon';
import { useNextHashQueryParams } from '@hub/hooks/useNextQueryParams';
import { getRedirectLinkToProductDetails } from '@hub/bearing-hub/common/link.factory';
import { ProductTile } from '@hub/common/ProductTile/ProductTile';
import { useAddToShoppingCart } from '@hub/bearing-hub/common/basket/useAddToShoppingCart';
import { useSessionStore } from '@hub/hooks/states/useSessionStore';
import { DEFAULT_QUANTITY } from '@hub/bearing-hub/common/constants';
import { formatPrice } from '@hub/libs/product-price-utils';
import { createNumberFormatter } from '@icommerce/i18n';
import { ProgressIndicator, ProgressShape, ProgressThickness } from '@icommerce/ui';

export const IbhProductTile = ({
  data,
  maxLifeTimeReference,
  isCustom = false,
  selectedShape,
  setActiveModal,
  unitSystem,
  clientReady,
  lifetime,
  isLifetimeLoading,
}: ProductTableProps) => {
  const router = useRouter();
  const query = useNextHashQueryParams();
  const hashParams = clientReady ? query : {};
  const locale = getLocale(router);
  const { t, i18n } = useTranslation();
  const session = useSessionStore(state => state.session);
  const lifetimeFactor =
    lifetime > 0 && maxLifeTimeReference > 0
      ? Math.min(80, Math.round(Math.sqrt(lifetime / maxLifeTimeReference) * 80))
      : 0;
  const { addToShoppingCart, addToBasketInProgress } = useAddToShoppingCart({
    session,
    quantity: Number(hashParams.quantity) || DEFAULT_QUANTITY,
    shape: selectedShape.shapeKey,
    setupCostIsLoading: false,
    i18n,
  });

  return (
    <ProductTile
      image={
        data.widenShortKey ? (
          <WidenImage
            shortCode={data.widenShortKey}
            title={data.articleNumber}
            filename={data.widenShortKey}
            width={216}
            height={144}
          />
        ) : (
          <WidenImage
            shortCode={selectedShape.widenShortKeyIcon}
            title={data.articleNumber}
            filename={selectedShape.widenShortKeyIcon}
            width={216}
            height={144}
          />
        )
      }
      productInfo={
        <>
          <div className="font-bold">{data.material.name}</div>
          <div>{isCustom ? t(`CUSTOM_PART`, { ns: 'bearing-hub/bearingHub' }) : data.articleNumber}</div>
          <div>
            {t(`PRODUCTION_METHODS.${data.productionMethod.replace(/-/gi, '_')}`, { ns: 'bearing-hub/bearingHub' })}
          </div>
          {typeof data.unitPrice?.value !== 'undefined' &&
            !Number.isNaN(data.unitPrice.value) &&
            data.unitPrice.value > 0 && (
              <div className="font-medium mt-5 text-lg">
                {formatPrice(data.unitPrice.value, locale, data.unitPrice.unit)} /{' '}
                {t(`HEADLINES.PIECE`, { ns: 'bearing-hub/bearingHub' })}
                {typeof data.setupCost?.value !== 'undefined' &&
                  !Number.isNaN(data.setupCost.value) &&
                  data.setupCost.value > 0 && (
                    <p className="text-xxs mt-1 text-gray-300 whitespace-nowrap">
                      {t(`PRICE_PLUS_SETUP_COSTS`, {
                        price: formatPrice(data.setupCost.value, locale, data.setupCost.unit),
                        ns: 'bearing-hub/bearingHub',
                      })}
                    </p>
                  )}
              </div>
            )}
        </>
      }
      ctaButton={
        <>
          <Button
            useInternalRouting
            href={getRedirectLinkToProductDetails(data, selectedShape, unitSystem, hashParams, isCustom, locale)}
            text={t(`GOTO_PRODUCT_PAGE`, { ns: 'bearing-hub/bearingHub' })}
            className="w-full"
          />
          <Button
            disabled={!clientReady}
            text={t('BUY_BOX.ADD_TO_CART', { ns: 'product/buyBox' })}
            className="w-full"
            onClick={() => addToShoppingCart(data)}
            loading={addToBasketInProgress}
          />
        </>
      }
    >
      <>
        <div className="w-full flex flex-row mb-6">
          <div className="w-[40%] font-bold">{t(`HEADLINES.DELIVERY_TIME`, { ns: 'bearing-hub/bearingHub' })}:</div>
          <div className="ml-4">
            {data.deliveryTimeInDays && !Number.isNaN(data.deliveryTimeInDays) && (
              // eslint-disable-next-line react/jsx-no-useless-fragment
              <>
                {data.deliveryTimeInDays < 0 ? (
                  <span className="whitespace-nowrap">
                    {t(`DELIVERY_TIMES.UPON_REQUEST`, { ns: 'bearing-hub/bearingHub' })}
                  </span>
                ) : (
                  <span className="whitespace-nowrap">
                    {data.deliveryTimeInDays}{' '}
                    {t(`HEADLINES.DAYS`, { count: data.deliveryTimeInDays, ns: 'bearing-hub/bearingHub' })}
                  </span>
                )}
              </>
            )}
          </div>
        </div>
        {isLifetimeLoading ? (
          <div className="w-full flex flex-row mb-10 items-center">
            <div className="w-[40%] font-bold">{t(`HEADLINES.LIFETIME`, { ns: 'bearing-hub/bearingHub' })}:</div>
            <div className="relative ml-4">
              <ProgressIndicator
                label={t('COMMON.LOADING', { ns: 'free-form-hub/freeFormHubCommon' })}
                shape={ProgressShape.Circular}
                thickness={ProgressThickness.Thin}
                size={12.5}
              />
            </div>
          </div>
        ) : (
          lifetime &&
          !Number.isNaN(lifetime) && (
            <div className="w-full flex flex-row mb-10 items-center">
              <div className="w-[40%] font-bold">{t(`HEADLINES.LIFETIME`, { ns: 'bearing-hub/bearingHub' })}:</div>
              <div className="relative ml-4">
                {Math.ceil(lifetime) < 0 ? (
                  <span className="whitespace-nowrap">
                    {t(`LIFETIME.UPON_REQUEST`, { ns: 'bearing-hub/bearingHub' })}
                  </span>
                ) : (
                  <span className="whitespace-nowrap">
                    {createNumberFormatter(locale).format(Math.ceil(lifetime))} h
                  </span>
                )}
                {Math.ceil(lifetime) >= 0 && (
                  <div className="absolute left-0 rounded-full w-[100px] h-[4px] mr-16 mt-1 bg-[#C8DFE9]">
                    <div
                      className="absolute left-0 rounded-full h-[4px] bg-[#2480A8]"
                      style={{
                        width: `${lifetimeFactor}%`,
                      }}
                    />
                  </div>
                )}
              </div>
              <div
                className="ml-4"
                data-testid="show-life-time-modal"
                onClick={event => {
                  event.stopPropagation();
                  setActiveModal?.(true);
                }}
              >
                <Icon size={6} id={IconID.INFO_CIRCLE_GRAY} />
              </div>
            </div>
          )
        )}
        <div className="w-full flex flex-col">
          <div className="min-w-[40%] font-bold mb-6">
            {t(`HEADLINES.DIMENSIONS`, { ns: 'bearing-hub/bearingHub', unit: 'mm' })}:
          </div>
          <div className="flex flex-wrap">
            {data?.dimensions ? (
              <BearingTableDimensions
                selectedShape={selectedShape}
                showHeader
                data={data}
                locale={locale}
                unitSystem={unitSystem}
              />
            ) : null}
          </div>
        </div>
      </>
    </ProductTile>
  );
};
